import { memo, useMemo, useState } from 'react';
import { capitalize } from 'lodash-es';
import { PracticeAnalyticsAggregations, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks, DemoChip } from '@frontend/analytics-shared';
import { LocationChip } from '@frontend/analytics-shared';
import {
  BarChartAppearance,
  BarChartData,
  Chart,
  CustomAxisTickParams,
  CustomTooltipTitle,
  sequentialChartColors,
  YAxisLabelValueTick,
} from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';

type Props = PracticeAnalyticsTypes.MetricProps & {
  data?: ReturnType<typeof PracticeAnalyticsAggregations.activePatientsDetails>;
  isLoading?: boolean;
};

const subBucketTotal = (subbucket: PracticeAnalyticsTypes.Buckets['subbuckets']) => {
  return subbucket.reduce((acc, { label, total }) => (label.toLowerCase() === 'other' ? acc : acc + total), 0);
};

export const collectAllInsuranceLabels = (
  data?: ReturnType<typeof PracticeAnalyticsAggregations.activePatientsDetails>
) => {
  return Object.values(data?.rawData || {}).reduce((acc, locationDetails) => {
    const { location } = locationDetails || {};
    return [...acc, ...(location?.patientDemographics?.details?.insuranceProviders.map(({ label }) => label) || [])];
  }, [] as string[]);
};

export const ActivePatientsMetricMulti = memo(({ data, isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

  const chartData = useMemo(() => {
    const entries = Object.entries(data?.rawData ?? {});
    const insuranceLabels = [...new Set(collectAllInsuranceLabels(data))];
    const totals: Record<string, number> = {};

    const ageGender: BarChartData = {
      groups: entries.map(([locationId, locationDetails]) => ({
        name: locationNames[locationId] || locationId,
        values:
          locationDetails?.location.patientDemographics?.buckets?.reduce((acc, { label, subbuckets }) => {
            const value = subBucketTotal(subbuckets);
            totals[label] = (totals[label] || 0) + value;
            return { ...acc, [label]: value };
          }, {}) || {},
      })),
    };

    const insurance: BarChartData = {
      groups: entries.map(([locationId, locationDetails]) => ({
        name: locationNames[locationId] || locationId,
        values:
          locationDetails?.location.patientDemographics?.details?.insuranceProviders?.reduce(
            (acc, { label, total }) => {
              totals['insured'] = (totals['insured'] || 0) + total;
              return { ...acc, [label]: total };
            },
            {}
          ) || {},
      })),
    };

    return {
      ageGender,
      insurance,
      insuranceColors: insuranceLabels.reduce(
        (acc, label, index) => ({ ...acc, [label]: sequentialChartColors[index] || theme.colors.neutral5 }),
        {}
      ),
      insuranceLabels: insuranceLabels.reduce((acc, label) => ({ ...acc, [label]: capitalize(label) }), {}),
      totals,
    };
  }, [locationNames, data]);

  const commonProps = useMemo(() => {
    return {
      appearance: {
        collectiveTooltip: true,
        customTooltipTitle: ({ groupName }: CustomTooltipTitle) => (
          <LocationChip locationName={groupName} noTruncating />
        ),
        customYAxisTick: ({ labels, groupName, ...rest }: CustomAxisTickParams) => {
          return (
            <YAxisLabelValueTick
              {...rest}
              label={labels?.[groupName] || groupName}
              onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
              xTransitionLabel={-190}
            />
          );
        },
        groupsGap: 24,
        layout: 'vertical' as BarChartAppearance['layout'],
        margin: { left: isLargeChart ? 140 : 40 },
        mode: 'stacked' as BarChartAppearance['mode'],
        showGridLines: true,
        showXAxis: true,
        showYAxis: true,
      },
    };
  }, [isLargeChart]);

  return (
    <>
      <Chart
        colors={{
          '0-12': theme.colors.secondary.seaweed30,
          '12-20': theme.colors.critical40,
          '21-30': theme.colors.primary50,
          '31-42': theme.colors.secondary.eggplant20,
          '43-55': theme.colors.warning50,
          '56-69': theme.colors.success20,
          '70+': theme.colors.secondary.eggplant50,
        }}
        isLoading={isLoading}
        labels={{
          '0-12': t('0-12'),
          '12-20': t('12-20'),
          '21-30': t('21-30'),
          '31-42': t('31-42'),
          '43-55': t('43-55'),
          '56-69': t('56-69'),
          '70+': t('70+'),
        }}
      >
        <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Patients by Age/Gender')} />
        <Chart.BarChart appearance={commonProps.appearance} data={chartData.ageGender} formatValue={formatters.value} />
        <Chart.Legends formatValue={formatters.value} values={chartData.totals} />
      </Chart>

      <Chart colors={chartData.insuranceColors} isLoading={isLoading} labels={chartData.insuranceLabels}>
        <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Patients by Insurance')} />
        <Chart.BarChart appearance={commonProps.appearance} data={chartData.insurance} formatValue={formatters.value} />
        <Chart.Legends
          customData={{
            insured: {
              color: theme.colors.primary40,
              label: t('Total'),
              value: formatters.value(chartData.totals.insured),
            },
          }}
        />
      </Chart>
    </>
  );
});

ActivePatientsMetricMulti.displayName = 'ActivePatientsMetricMulti';
